import { KnownUserDtoUserType } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useCallback, useState } from 'react';
import { useQueryGetProjectOptional } from '../../../../core/queries/projects.queries';
import useSearchParams from '../../../../hooks/use-search-params.hook';
import { InfiniteScrollProps } from '../../../../ui/components/pure/list-table-wrappers/infinite-table.component';
import { useInfiniteQueryDeliveriesList } from '../../../deliveries/deliveries.queries';
import { useQueryGetProjectModules } from '../../../project-modules/queries';
import { useUserByIdQuery } from '../../users.queries';
import { UserDetailsActions } from '../actions/user-details.actions';
import {
  deliveriesListInitialState,
  deliveriesListSearchParamOptions
} from '../components/user-deliveries-list/user-deliveries-list.component';
import { useUserStatusChange } from './use-user-status-change.hook';

export function useUserDetails(projectID: string, userID: string, childProjectID?: string) {
  const [currentAction, setCurrentAction] = useState<UserDetailsActions | null>(null);

  const { activateUser, deactivateUser } = useUserStatusChange(setCurrentAction, userID);

  const setAction = useCallback(
    (action: UserDetailsActions | null) => {
      switch (action) {
        case UserDetailsActions.ACTIVATE_GROUP_MEMBER:
          return activateUser();
        case UserDetailsActions.DEACTIVATE_GROUP_MEMBER:
          return deactivateUser();
        default:
          return setCurrentAction(action);
      }
    },
    [activateUser, deactivateUser]
  );

  const user = useUserByIdQuery(projectID, userID, childProjectID);

  // Couriers redirect to their own page but still land here for a render, from an older link or a
  // search result. Nothing project-scoped is asked for meanwhile: the users endpoint resolves their
  // project to the carriers anchor, which their own admins cannot read.
  const isCourier = user.data?.userType === KnownUserDtoUserType.Courier;

  const retailer = useQueryGetProjectOptional(user.data?.projectId, {
    enabled: !!user.data && !isCourier
  });

  const [formState, queryState, onFormInput] = useSearchParams(
    deliveriesListInitialState,
    deliveriesListSearchParamOptions
  );

  const deliveries = useInfiniteQueryDeliveriesList(isCourier ? undefined : user.data?.projectId, 1, {
    ...queryState,
    userID: user.data?.id
  });

  const infiniteScroll: InfiniteScrollProps = {
    hasNextPage: deliveries.hasNextPage || false,
    isFetchingNextPage: deliveries.isFetchingNextPage,
    fetchNextPage: deliveries.fetchNextPage,
    onScrollToBottom() {
      deliveries.fetchNextPage();
    }
  };

  const projectModules = useQueryGetProjectModules(isCourier ? undefined : user.data?.projectId);

  return {
    user,
    retailer,
    isCourier,
    deliveries,
    deliveriesInfiniteScroll: infiniteScroll,
    formState,
    onFormInput,
    actions: {
      currentAction,
      setAction
    },
    projectModules
  } as const;
}
